import React, { useLayoutEffect, useState, useRef, useEffect } from "react";
import {
  Layout,
  Card,
  Button,
  Space,
  Typography,
  Tag,
  Table,
  Divider,
  DatePicker,
  Spin,
  Select,
  Row,
  Col,
} from "antd";
import LayoutHeader from "../../inc/LayoutHeader";
import LayoutFooter from "../../inc/LayoutFooter";
import { getAPI } from "../../../utils/common";
import { KITCHEN_PURCHASE_REQUEST_REPORT } from "../../../constants/api";
import LS_SERVICE from "../../../utils/localStorage";
import dayjs from "dayjs";
import { PROJECT_NAME } from "../../../constants/constent";

const { Content } = Layout;
const { Title, Text } = Typography;

function KitchenPurchaseRequestReport() {
  const printRef = useRef();

  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [printMode, setPrintMode] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [statusFilter, setStatusFilter] = useState(null);
  const [dates, setDates] = useState([
    dayjs().subtract(1, "day"),
    dayjs().add(1, "day"),
  ]);

  const { company_name } = LS_SERVICE.get("setting") || {};

  // =========================
  // FETCH API
  // =========================
  const fetchData = () => {
    let url = KITCHEN_PURCHASE_REQUEST_REPORT;
    if (dates.length === 2) {
      url += `?date_from=${dates[0].format("YYYY-MM-DD")}&date_to=${dates[1].format("YYYY-MM-DD")}`;
    }
    setLoading(true);
    getAPI(url, (res) => {
      if (res.status) {
        setData(res.data || []);
        setSummary(res.summary || {});
      }
      setLoading(false);
    });
  };

  useLayoutEffect(() => {
    fetchData();
  }, []);

  // =========================
  // PRINT HANDLER
  // =========================
  const handlePrint = () => {
    const printContents = printRef.current.innerHTML;
    const originalContents = document.body.innerHTML;
    document.body.innerHTML = printContents;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  // =========================
  // AUTO EXPAND IN PRINT MODE
  // =========================
  useEffect(() => {
    if (printMode) {
      setExpandedRowKeys((data || []).map((d) => d.pr_id));
    } else {
      setExpandedRowKeys([]);
    }
  }, [printMode, data]);

  // =========================
  // FILTERED DATA
  // =========================
  const filteredData = (data || []).filter((item) => {
    if (!statusFilter) return true;
    return item.status === statusFilter;
  });

  // =========================
  // ITEM TABLE COLUMNS
  // =========================
  const columns = [
    { title: "Item Code", dataIndex: "item_code" },
    { title: "Item Name", dataIndex: "item_name" },
    { title: "Requested", dataIndex: "requested" },
    { title: "Approved", dataIndex: "approved" },
    { title: "Remark", dataIndex: "item_remark" },
  ];

  return (
    <Layout>
      <LayoutHeader />
      <Content>
        <Card
          title="Purchase Request Report"
          extra={
            <Space>
              {/* DATE PICKER */}
              <DatePicker.RangePicker
                value={dates}
                onChange={(v) => setDates(v || [])}
              />

              {/* STATUS FILTER */}
              <Select
                placeholder="Filter Status"
                allowClear
                style={{ width: 150 }}
                onChange={(v) => setStatusFilter(v)}
              >
                <Select.Option value="Pending">Pending</Select.Option>
                <Select.Option value="Purchased">Purchased</Select.Option>
                <Select.Option value="Reject">Reject</Select.Option>
              </Select>

              <Button type="primary" onClick={fetchData}>
                Filter
              </Button>

              <Button
                type="primary"
                onClick={() => {
                  setPrintMode(true);
                  setTimeout(() => {
                    handlePrint();
                    setPrintMode(false);
                  }, 500);
                }}
              >
                Print Report
              </Button>
            </Space>
          }
        >
          <div ref={printRef}>
            {/* PRINT HEADER */}
            {printMode && (
              <div style={{ textAlign: "center" }}>
                <Title level={4}>{company_name || PROJECT_NAME}</Title>
                <Title level={5}>Purchase Request Report</Title>
                <Text>
                  {dates[0]?.format("DD MMM YYYY")} -{" "}
                  {dates[1]?.format("DD MMM YYYY")}
                </Text>
              </div>
            )}

            <Divider />

            {/* SUMMARY */}
            <Space style={{ marginBottom: 10 }}>
              <Tag color="blue">
                Total Requests: {summary.total_requests || 0}
              </Tag>
              <Tag color="orange">Pending: {summary.pending || 0}</Tag>
              <Tag color="green">Purchased: {summary.purchased || 0}</Tag>
              <Tag color="red">Reject: {summary.reject || 0}</Tag>
            </Space>

            <Divider />

            {/* MAIN TABLE */}
            <Spin spinning={loading}>
              <Table
                dataSource={filteredData}
                rowKey="pr_id"
                bordered
                pagination={false}
                expandable={{
                  expandedRowKeys: expandedRowKeys,
                  onExpand: (expanded, record) => {
                    setExpandedRowKeys((prev) =>
                      expanded
                        ? [...prev, record.pr_id]
                        : prev.filter((id) => id !== record.pr_id),
                    );
                  },
                  expandedRowRender: (pr) => (
                    <div>
                      {pr.remark && (
                        <Text>
                          <b>Remark:</b> {pr.remark || "-"}
                        </Text>
                      )}

                      <Table
                        style={{ marginTop: 10 }}
                        dataSource={pr.items || []}
                        columns={columns}
                        pagination={false}
                        rowKey={(r, i) => i}
                        size="small"
                        bordered
                      />
                    </div>
                  ),
                }}
                columns={[
                  {
                    title: "PR ID",
                    dataIndex: "pr_id",
                    render: (v) => <b>PR #{v}</b>,
                  },
                  {
                    title: "Status",
                    dataIndex: "status",
                    render: (v) => <Tag color="blue">{v}</Tag>,
                  },
                  {
                    title: "Priority",
                    dataIndex: "priority",
                    render: (v) => <Tag color="purple">{v}</Tag>,
                  },
                  {
                    title: "Date",
                    dataIndex: "date",
                    render: (v) => dayjs(v).format("DD MMM YYYY"),
                  },
                  {
                    title: "Requested By",
                    dataIndex: "requested_by",
                  },
                ]}
                className={printMode ? "no-expand-icon" : ""}
              />
            </Spin>
          </div>
        </Card>
      </Content>
      <LayoutFooter />
    </Layout>
  );
}

export default KitchenPurchaseRequestReport;
